import { useState } from "react";
import { Box, Button, Icon, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { MdLockOpen, MdLock, MdDeleteSweep, MdAccountBalance } from "react-icons/md";
import FreezeWalletDialog from "../dialogs/FreezeWalletDialog";
import ConfiscateWalletDialog from "../dialogs/ConfiscateWalletDialog";
import BankTransferDialog from "../dialogs/BankTransferDialog";
import { SUPER_ADMIN_RIGHT } from "../groupsConstants";
import { SectionTitle, KvRow, EmptyState, InfoBanner } from "../groupsUi";

const BANK_STATUS_LABELS = {
    draft: { label: "En attente certification", color: "#ff9800" },
    pending_connect: { label: "Activation Stripe en cours", color: "#29b6f6" },
    active: { label: "Compte actif", color: "#4caf50" },
    failed: { label: "Activation échouée", color: "#ef5350" },
};

/**
 * Returns a label/color descriptor for the banking status, or "Pas de compte"
 * when banking is empty / undefined.
 * @param {Object} banking
 * @returns {{label:string,color:string}}
 */
function bankStatusInfo(banking) {
    if (!banking || Object.keys(banking).length === 0) {
        return { label: "Pas de compte", color: "secondaryGray.500" };
    }
    return BANK_STATUS_LABELS[banking.status] || { label: "Statut inconnu", color: "secondaryGray.500" };
}

/**
 * Formatted balance display.
 * @param {{wallet:Object}} props
 */
function BalanceDisplay({ wallet }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    const amount = Number(wallet?.balance ?? 0).toFixed(2);
    const currency = (wallet?.currency || "").toUpperCase();
    return (
        <Box>
            <Text color={labelColor} fontSize="0.72rem" textTransform="uppercase" letterSpacing="0.5px">
                Solde disponible
            </Text>
            <Text color={valueColor} fontSize="1.6rem" fontWeight="700">{amount} {currency}</Text>
        </Box>
    );
}

/**
 * Action buttons row for the wallet card.
 * @param {Object} props - { wallet, isSuperAdmin, onFreeze, onTransfer, onConfiscate }
 */
function WalletActions({ wallet, isSuperAdmin, onFreeze, onTransfer, onConfiscate }) {
    return (
        <Stack direction={{ base: "column", sm: "row" }} spacing="12px" flexWrap="wrap">
            <Button variant="outline" leftIcon={<Icon as={wallet.frozen ? MdLockOpen : MdLock} />} onClick={onFreeze}>
                {wallet.frozen ? "Dégeler" : "Geler"}
            </Button>
            <Button colorScheme="brand" leftIcon={<Icon as={MdAccountBalance} />} onClick={onTransfer} isDisabled={wallet.frozen}>
                Virement bancaire
            </Button>
            {isSuperAdmin && (
                <Button colorScheme="red" leftIcon={<Icon as={MdDeleteSweep} />} onClick={onConfiscate}>
                    Confisquer
                </Button>
            )}
        </Stack>
    );
}

/**
 * Banking section showing the activation status of the group's bank account
 * and the Stripe rejection reason when status === 'failed'.
 * @param {{detail:Object}} props
 */
function BankingSection({ detail }) {
    const banking = detail?.banking;
    const info = bankStatusInfo(banking);
    return (
        <Box>
            <SectionTitle>Compte bancaire</SectionTitle>
            <KvRow label="Statut" value={info.label} valueColor={info.color} />
            {banking?.account_holder_name && <KvRow label="Titulaire" value={banking.account_holder_name} />}
            {banking?.status === "failed" && banking?.admin_message && (
                <KvRow label="Raison du rejet" value={banking.admin_message} valueColor="#ef5350" />
            )}
        </Box>
    );
}

/**
 * Wallet tab content (balance, freeze toggle, confiscate, bank transfer).
 * @param {Object} props - { detail, onRefresh, onChanged, userRight }
 */
function WalletTab({ detail, onRefresh, onChanged, userRight }) {
    const wallet = detail?.wallets?.[0];
    const cardBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const [freezeOpen, setFreezeOpen] = useState(false);
    const [confiscateOpen, setConfiscateOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const isSuperAdmin = Number(userRight) >= SUPER_ADMIN_RIGHT;
    const onSuccess = () => { onRefresh?.(); onChanged?.(detail); };

    if (!wallet) return <EmptyState>Aucun wallet rattaché à ce groupe.</EmptyState>;

    return (
        <Stack spacing="16px">
            <Stack spacing="12px" p="20px" borderRadius="12px" bg={cardBg}>
                {wallet.frozen && (
                    <InfoBanner>Wallet gelé{wallet.frozen_reason ? ` — ${wallet.frozen_reason}` : ""}</InfoBanner>
                )}
                <BalanceDisplay wallet={wallet} />
                <WalletActions wallet={wallet} isSuperAdmin={isSuperAdmin}
                    onFreeze={() => setFreezeOpen(true)} onTransfer={() => setTransferOpen(true)} onConfiscate={() => setConfiscateOpen(true)} />
            </Stack>
            <BankingSection detail={detail} />
            <FreezeWalletDialog open={freezeOpen} groupId={detail?.id} wallet={wallet} onClose={() => setFreezeOpen(false)} onToggled={() => { setFreezeOpen(false); onSuccess(); }} />
            <ConfiscateWalletDialog open={confiscateOpen} groupId={detail?.id} wallet={wallet} onClose={() => setConfiscateOpen(false)} onConfiscated={() => { setConfiscateOpen(false); onSuccess(); }} />
            <BankTransferDialog open={transferOpen} groupId={detail?.id} wallet={wallet} members={detail?.members || []} onClose={() => setTransferOpen(false)} onTransferred={() => { setTransferOpen(false); onSuccess(); }} />
        </Stack>
    );
}

export default WalletTab;
